import { supabase } from '@/lib/supabaseClient'
import { openWorldCanonError } from '@/api/adapters/supabase/open-world-errors'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import { OpenWorldPersistenceError } from '@/lib/openWorlds/persistence'
import { isOpenWorldAttemptUuid } from '@/lib/openWorlds/attemptPersistence'
import { isOpenWorldJournalJson, isOpenWorldJournalRecord } from '@/lib/openWorlds/journalPersistence'
import { validOpenWorldConversationSettings } from '@/lib/openWorlds/conversation'
import { validateOpenWorldConfiguration } from '@/lib/openWorlds/configuration'
import { validateOpenWorldNote, type OpenWorldNoteTurn, type BeginOpenWorldNoteRequest,
  type FinishOpenWorldNoteRequest, type WriteOpenWorldNoteRequest, type BeginOpenWorldNoteResponse } from '@/lib/openWorlds/notes'

export function mapOpenWorldNoteTurn(value: unknown): OpenWorldNoteTurn {
  const fail = (): never => { throw new OpenWorldPersistenceError('backend', 'Malformed story note turn.') }
  if (!isOpenWorldJournalRecord(value) || Object.keys(value).some(key => !['attemptId','status','note','error','deadlineAt','finishedAt','noteId','sequence','manual','boundary','playerMessageId'].includes(key)) ||
    !isOpenWorldAttemptUuid(value.attemptId) || !['queued', 'running', 'completed', 'failed'].includes(value.status as string) ||
    !['note', 'error', 'deadlineAt', 'finishedAt'].every((key) => value[key] === null || typeof value[key] === 'string')) fail()
  if (!isOpenWorldJournalRecord(value)) return fail()
  if (value.boundary === 'before' && !isOpenWorldAttemptUuid(value.playerMessageId)) fail()
  if (value.noteId !== undefined && !isOpenWorldAttemptUuid(value.noteId)) fail()
  if (value.sequence !== undefined && (!Number.isSafeInteger(value.sequence) || (value.sequence as number) < 1)) fail()
  if (value.manual !== undefined && typeof value.manual !== 'boolean') fail()
  if (value.boundary !== undefined && !['before','opening'].includes(value.boundary as string)) fail()
  if (value.boundary === 'opening' && value.manual !== true) fail()
  const row = value as unknown as OpenWorldNoteTurn
  for (const date of [row.deadlineAt, row.finishedAt]) if (date !== null && !Number.isFinite(Date.parse(date))) fail()
  if (row.status === 'completed') {
    if (row.note === null || row.error !== null) fail()
    try { validateOpenWorldNote(row.note!) } catch { fail() }
  } else if (row.note !== null || (row.status === 'failed' ? !row.error : row.error !== null)) fail()
  if (row.status === 'running' && row.deadlineAt === null) fail()
  return { ...row }
}

function validIdentity(request: BeginOpenWorldNoteRequest) {
  if ((request.noteId !== undefined && !isOpenWorldAttemptUuid(request.noteId)) || !isOpenWorldAttemptUuid(request.attemptId) || !isOpenWorldAttemptUuid(request.executionToken)) {
    throw new OpenWorldPersistenceError('invalid_input', 'Story note identity must use UUIDs.')
  }
}

async function rpc(name: string, args: Record<string, unknown>): Promise<unknown> {
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc(name, args)
  if (error) throw openWorldCanonError(error) ?? new OpenWorldPersistenceError(error.message?.includes('OW_CONFLICT') ? 'conflict' : 'backend',
    'The story note change could not be saved.', { cause: error })
  return data
}

export async function beginOpenWorldNote(request: BeginOpenWorldNoteRequest): Promise<BeginOpenWorldNoteResponse> {
  validIdentity(request)
  const value = await rpc('rpc_begin_open_world_note_v4', {
    p_note_id: request.noteId ?? null,
    p_attempt_id: request.noteId ? null : request.attemptId,
    p_execution_token: request.executionToken, p_expected_revision:request.expectedPlaythroughRevision ?? null,
  })
  return mapNoteClaim(value)
}

function mapNoteClaim(value: unknown): BeginOpenWorldNoteResponse {
  if (!isOpenWorldJournalRecord(value) || typeof value.replayed !== 'boolean') {
    throw new OpenWorldPersistenceError('backend', 'Malformed story note acknowledgement.')
  }
  const response: BeginOpenWorldNoteResponse = { noteTurn: mapOpenWorldNoteTurn(value.noteTurn), replayed: value.replayed }
  if (value.invocation !== undefined && value.invocation !== null) {
    const invocation = value.invocation
    if (!isOpenWorldJournalRecord(invocation) || !Number.isInteger(invocation.storytellerBotId) ||
      (invocation.storytellerBotId as number) < 1 || !validateOpenWorldConfiguration(invocation.configuration).success ||
      (invocation.conversationSettings !== undefined && !validOpenWorldConversationSettings(invocation.conversationSettings)) ||
      (invocation.storytellerAdvancedPrompt !== undefined && typeof invocation.storytellerAdvancedPrompt !== 'string') ||
      (invocation.canonCaptured !== undefined && typeof invocation.canonCaptured !== 'boolean')) {
      throw new OpenWorldPersistenceError('backend', 'Malformed story note configuration.')
    }
    response.invocation = { configuration: invocation.configuration as NonNullable<BeginOpenWorldNoteResponse['invocation']>['configuration'],
      ...(invocation.canonCaptured === undefined ? {} : { canonCaptured: invocation.canonCaptured === true }),
      storytellerBotId: invocation.storytellerBotId as number,
      ...(typeof invocation.storytellerAdvancedPrompt === 'string' ? {storytellerAdvancedPrompt:invocation.storytellerAdvancedPrompt} : {}),
      ...(invocation.conversationSettings === undefined ? {} : {conversationSettings: invocation.conversationSettings as NonNullable<BeginOpenWorldNoteResponse['invocation']>['conversationSettings']}) }
  }
  return response
}

export async function finishOpenWorldNote(request: FinishOpenWorldNoteRequest): Promise<OpenWorldNoteTurn> {
  validIdentity(request)
  if ((request.note === null) === (request.error === null) || !isOpenWorldJournalRecord(request.evidence) ||
    !isOpenWorldJournalJson(request.evidence) || new TextEncoder().encode(JSON.stringify(request.evidence)).length > 262144 ||
    (request.error !== null && (!request.error.trim() || new TextEncoder().encode(request.error).length > 4096))) {
    throw new OpenWorldPersistenceError('invalid_input', 'Story note result is invalid.')
  }
  if (request.note !== null) validateOpenWorldNote(request.note)
  return mapOpenWorldNoteTurn(await rpc(request.noteId ? 'rpc_finish_open_world_note_v3' : 'rpc_finish_open_world_note_v2', {
    ...(request.noteId ? {p_note_id:request.noteId} : {p_attempt_id: request.attemptId}), p_execution_token: request.executionToken,
    p_note: request.note, p_error: request.error, p_evidence: request.evidence,
  }))
}

export async function recoverOpenWorldNote(attemptId: string, noteId?: string): Promise<OpenWorldNoteTurn> {
  if (!isOpenWorldAttemptUuid(attemptId) || (noteId !== undefined && !isOpenWorldAttemptUuid(noteId))) throw new OpenWorldPersistenceError('invalid_input', 'Story note identity must be a UUID.')
  return mapOpenWorldNoteTurn(await rpc(noteId ? 'rpc_recover_open_world_note_v3' : 'rpc_recover_open_world_note_v2', noteId ? {p_note_id: noteId} : { p_attempt_id: attemptId }))
}

export async function writeOpenWorldNote(request: WriteOpenWorldNoteRequest): Promise<BeginOpenWorldNoteResponse> {
  if (![request.playthroughId, request.noteId, request.executionToken].every(isOpenWorldAttemptUuid) ||
    (request.expectedHeadId !== null && !isOpenWorldAttemptUuid(request.expectedHeadId)) ||
    !Number.isSafeInteger(request.expectedPlaythroughRevision) || request.expectedPlaythroughRevision < 1 ||
    !Number.isSafeInteger(request.expectedDefinitionRevision) || request.expectedDefinitionRevision < 1) {
    throw new OpenWorldPersistenceError('invalid_input', 'Story note request is invalid.')
  }
  return mapNoteClaim(await rpc('rpc_write_open_world_note_v2', {
    p_playthrough_id:request.playthroughId, p_note_id:request.noteId, p_execution_token:request.executionToken,
    p_expected_revision:request.expectedPlaythroughRevision, p_expected_head_id:request.expectedHeadId,
    p_expected_definition_revision:request.expectedDefinitionRevision,
  }))
}

export async function getOpenWorldNoteEvidence(noteId: string): Promise<Record<string, unknown>> {
  if (!isOpenWorldAttemptUuid(noteId)) throw new OpenWorldPersistenceError('invalid_input', 'Story note ID must be a UUID.')
  const result = await rpc('rpc_get_open_world_note_evidence_v1', {p_note_id:noteId})
  if (!isOpenWorldJournalRecord(result)) throw new OpenWorldPersistenceError('backend', 'Malformed story note evidence.')
  return result
}
